import React, { useState } from "react";

import { toast } from "react-toastify";
import {
	FlaskConical,
	Loader2,
	Pencil,
	Plus,
	Trash2,
	Wrench,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// A ferramenta que o agente pode chamar: uma API de fora (com credencial e
// requisição montada) ou uma ação interna do próprio atendimento.
//
// Tudo o que a tela manda passa pela validação do backend. Aqui a concern é
// só não mandar lixo: JSON quebrado é recusado aqui, com o motivo na tela, e
// não pelo erro genérico do servidor.

const MODOS = [
	{ value: "direto", rotulo: "Executa direto" },
	{ value: "confirma_cliente", rotulo: "Confirma com o cliente" },
	{ value: "aprovacao_atendente", rotulo: "Aprovação do atendente" },
];

const STATUS = [
	{ value: "active", rotulo: "Ativa" },
	{ value: "draft", rotulo: "Rascunho" },
];

const METODOS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const METODOS_COM_CORPO = ["POST", "PUT", "PATCH"];

const ACOES_INTERNAS = [
	{ value: "transferir_fila", rotulo: "Transferir de fila" },
	{ value: "adicionar_etiqueta", rotulo: "Aplicar etiqueta" },
	{ value: "chamar_humano", rotulo: "Chamar atendente" },
	{ value: "iniciar_fluxo", rotulo: "Iniciar fluxo" },
];

const TIPOS_DE_PARAMETRO = [
	{ value: "string", rotulo: "Texto" },
	{ value: "number", rotulo: "Número" },
	{ value: "integer", rotulo: "Inteiro" },
	{ value: "boolean", rotulo: "Verdadeiro ou falso" },
];

// "Preencher com" é o que o motor faz sozinho: a IA não vê estes parâmetros
// e não pode escolher o telefone de outro cliente.
const ORIGENS_FIXAS = [
	{ value: "nenhum", rotulo: "Nada" },
	{ value: "contact.number", rotulo: "Telefone do contato" },
	{ value: "contact.name", rotulo: "Nome do contato" },
	{ value: "contact.email", rotulo: "E-mail do contato" },
	{ value: "ticket.id", rotulo: "Número do atendimento" },
];

// A ação interna que precisa de um id: sem fila, etiqueta ou fluxo a ação não
// tem para onde agir.
const CHAVE_DO_ALVO = {
	transferir_fila: "queueId",
	adicionar_etiqueta: "tagId",
	iniciar_fluxo: "flowId",
};

const rotuloDoModo = (modo) =>
	MODOS.find((m) => m.value === modo)?.rotulo || modo;

const rotuloDaAcao = (acao) =>
	ACOES_INTERNAS.find((a) => a.value === acao)?.rotulo || acao;

const PARAMETRO_VAZIO = {
	name: "",
	type: "string",
	required: false,
	description: "",
	fixedFrom: "",
};

const FORMULARIO_VAZIO = {
	id: null,
	name: "",
	description: "",
	kind: "http",
	executionMode: "direto",
	status: "active",
	method: "GET",
	url: "",
	credentialId: "",
	headers: "",
	bodyTemplate: "",
	responseMap: "",
	parameters: [],
	internalAction: "transferir_fila",
	internalTarget: "",
};

// JSON quebrado é recusado aqui, com o motivo na tela, e não pelo erro
// genérico do servidor.
const lerJson = (texto) => {
	const limpo = String(texto || "").trim();

	if (!limpo) return { ok: true, valor: null };

	try {
		return { ok: true, valor: JSON.parse(limpo) };
	} catch {
		return { ok: false, valor: null };
	}
};

const AbaFerramentas = ({
	ferramentas = [],
	credenciais = [],
	aoMudar,
	aviso,
}) => {
	const [modal, setModal] = useState(false);
	const [form, setForm] = useState(FORMULARIO_VAZIO);
	const [erros, setErros] = useState({});
	const [salvando, setSalvando] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);

	const [filas, setFilas] = useState([]);
	const [etiquetas, setEtiquetas] = useState([]);
	const [fluxos, setFluxos] = useState([]);

	const [testeParams, setTesteParams] = useState({});
	const [teste, setTeste] = useState({ rodando: false, resultado: "", falhou: false });
	const [permitirAcao, setPermitirAcao] = useState(false);

	// A ferramenta como está no servidor: o teste chama a versão gravada, não
	// o que está no formulário.
	const ferramentaSalva = ferramentas.find((t) => t.id === form.id);
	const parametrosVisiveis = (ferramentaSalva?.parameters || []).filter(
		(parametro) => !parametro.fixedFrom
	);

	const chaveDoAlvo = CHAVE_DO_ALVO[form.internalAction];

	// Fila, etiqueta e fluxo: os alvos das ações internas. Cada um vem da sua
	// rota, e uma lista que falha não derruba as outras.
	const carregarAlvos = async () => {
		const [q, t, f] = await Promise.all([
			api.get("/queue").catch(() => ({ data: [] })),
			api.get("/tags").catch(() => ({ data: [] })),
			api.get("/flows").catch(() => ({ data: [] })),
		]);

		setFilas(Array.isArray(q.data) ? q.data : []);
		setEtiquetas(Array.isArray(t.data) ? t.data : []);
		setFluxos(Array.isArray(f.data) ? f.data : []);
	};

	const abrirNova = async () => {
		setForm(FORMULARIO_VAZIO);
		setErros({});
		setTesteParams({});
		setTeste({ rodando: false, resultado: "", falhou: false });
		setPermitirAcao(false);
		setModal(true);
		await carregarAlvos();
	};

	const abrirEdicao = async (ferramenta) => {
		const chave = CHAVE_DO_ALVO[ferramenta.internalAction];

		setForm({
			id: ferramenta.id,
			name: ferramenta.name || "",
			description: ferramenta.description || "",
			kind: ferramenta.kind || "http",
			executionMode: ferramenta.executionMode || "direto",
			status: ferramenta.status || "active",
			method: ferramenta.method || "GET",
			url: ferramenta.url || "",
			credentialId: ferramenta.credentialId
				? String(ferramenta.credentialId)
				: "",
			headers: ferramenta.headers
				? JSON.stringify(ferramenta.headers, null, 2)
				: "",
			bodyTemplate: ferramenta.bodyTemplate
				? JSON.stringify(ferramenta.bodyTemplate, null, 2)
				: "",
			responseMap: (ferramenta.responseMap || []).join("\n"),
			parameters: (ferramenta.parameters || []).map((parametro) => ({
				name: parametro.name || "",
				type: parametro.type || "string",
				required: Boolean(parametro.required),
				description: parametro.description || "",
				fixedFrom: parametro.fixedFrom || "",
			})),
			internalAction: ferramenta.internalAction || "transferir_fila",
			internalTarget:
				chave && ferramenta.internalConfig?.[chave]
					? String(ferramenta.internalConfig[chave])
					: "",
		});
		setErros({});
		setTesteParams({});
		setTeste({ rodando: false, resultado: "", falhou: false });
		setPermitirAcao(false);
		setModal(true);
		await carregarAlvos();
	};

	const atualizarParametro = (indice, mudanca) => {
		setForm((atual) => ({
			...atual,
			parameters: atual.parameters.map((parametro, posicao) =>
				posicao === indice ? { ...parametro, ...mudanca } : parametro
			),
		}));
	};

	const removerParametro = (indice) => {
		setForm((atual) => ({
			...atual,
			parameters: atual.parameters.filter((_, posicao) => posicao !== indice),
		}));
	};

	const montarParametros = () =>
		form.parameters
			.filter((parametro) => parametro.name.trim())
			.map((parametro) => ({
				name: parametro.name.trim(),
				type: parametro.type,
				required: Boolean(parametro.required),
				...(parametro.description.trim()
					? { description: parametro.description.trim() }
					: {}),
				...(parametro.fixedFrom ? { fixedFrom: parametro.fixedFrom } : {}),
			}));

	const salvar = async () => {
		const novosErros = {};

		const headersJson = lerJson(form.headers);
		if (!headersJson.ok) novosErros.headers = "JSON inválido.";

		const corpoJson = lerJson(form.bodyTemplate);
		if (!corpoJson.ok) novosErros.bodyTemplate = "JSON inválido.";

		if (!form.name.trim()) {
			novosErros.name = "Dê um nome para a ferramenta.";
		} else if (!/^[a-z][a-z0-9_]{1,62}$/.test(form.name.trim())) {
			// É o formato que a IA aceita para chamar: minúsculas, números e
			// sublinhado, começando por letra.
			novosErros.name =
				"Use minúsculas, números e _: é assim que a IA chama (ex.: segunda_via_boleto).";
		}

		// A descrição é por ela que a IA decide quando chamar a ferramenta:
		// curta demais, a IA não sabe o que fazer com ela.
		if (form.description.trim().length < 10) {
			novosErros.description =
				"Escreva pelo menos 10 caracteres: é por esta descrição que a IA decide quando usar.";
		}

		const nomes = montarParametros().map((parametro) => parametro.name);
		if (nomes.some((nome, posicao) => nomes.indexOf(nome) !== posicao)) {
			novosErros.parametros = "Há dois parâmetros com o mesmo nome.";
		}

		if (form.kind === "http") {
			if (!form.url.trim()) {
				novosErros.url = "Informe o endereço da API.";
			} else if (!/^https?:\/\//i.test(form.url.trim())) {
				novosErros.url = "O endereço precisa começar com http:// ou https://.";
			}
		}

		if (form.kind === "internal" && chaveDoAlvo && !form.internalTarget) {
			novosErros.internalTarget = "Escolha o destino da ação.";
		}

		if (Object.keys(novosErros).length) {
			setErros(novosErros);
			return;
		}

		setErros({});
		setSalvando(true);

		try {
			const dados = {
				name: form.name.trim(),
				description: form.description.trim(),
				kind: form.kind,
				executionMode: form.executionMode,
				status: form.status,
				parameters: montarParametros(),
			};

			if (form.kind === "http") {
				Object.assign(dados, {
					method: form.method,
					url: form.url.trim(),
					credentialId: form.credentialId
						? Number(form.credentialId)
						: null,
					headers: headersJson.valor,
					// Método sem corpo não leva corpo: guardar um corpo velho
					// num GET faria a tela e o servidor discordarem do que a
					// ferramenta faz.
					bodyTemplate: METODOS_COM_CORPO.includes(form.method)
						? corpoJson.valor
						: null,
					responseMap: form.responseMap
						.split("\n")
						.map((linha) => linha.trim())
						.filter(Boolean),
				});
			} else {
				Object.assign(dados, {
					internalAction: form.internalAction,
					internalConfig: chaveDoAlvo
						? { [chaveDoAlvo]: Number(form.internalTarget) }
						: {},
				});
			}

			if (dados.responseMap && !dados.responseMap.length) {
				dados.responseMap = null;
			}

			if (form.id) {
				await api.put(`/ai/tools/${form.id}`, dados);
			} else {
				await api.post("/ai/tools", dados);
			}

			toast.success(form.id ? "Ferramenta atualizada." : "Ferramenta criada.");
			setModal(false);
			aoMudar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/ai/tools/${selecionada.id}`);
			toast.success("Ferramenta removida.");
			aoMudar();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	const testar = async () => {
		if (!ferramentaSalva) return;

		setTeste({ rodando: true, resultado: "", falhou: false });

		try {
			const { data } = await api.post(`/ai/tools/${ferramentaSalva.id}/test`, {
				params: testeParams,
				permitirAcao,
			});

			setTeste({
				rodando: false,
				resultado: data?.conteudo || "",
				falhou: !data?.ok,
			});
		} catch (err) {
			setTeste({
				rodando: false,
				resultado: err?.response?.data?.error || "Falha ao testar a ferramenta.",
				falhou: true,
			});
		}
	};

	const alvoDaAcao =
		form.internalAction === "transferir_fila"
			? filas
			: form.internalAction === "adicionar_etiqueta"
				? etiquetas
				: fluxos;

	return (
		<div className="space-y-3">
			<ConfirmationModal
				title={selecionada && `Remover ${selecionada.name}?`}
				open={Boolean(confirmOpen)}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={excluir}
				confirmLabel="Remover"
			>
				O agente que usava esta ferramenta para de poder chamá-la. As
				pendências que ficaram pela metade são apagadas junto.
			</ConfirmationModal>

			{aviso ? (
				<div className="rounded-xl border bg-card py-16 text-center">
					<Wrench className="mx-auto h-8 w-8 text-muted-foreground/50" />
					<p className="mt-3 text-sm font-medium">{aviso}</p>
					<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
						Ferramentas fazem parte do módulo de agentes de IA. Fale com quem
						administra a sua conta para liberar o módulo.
					</p>
				</div>
			) : (
				<>
					<div className="flex flex-wrap items-center gap-2">
						<p className="min-w-0 flex-1 text-xs leading-relaxed text-muted-foreground">
							O que o agente pode consultar e o que ele pode fazer. Marque
							estas ferramentas no agente para ele poder usá-las.
						</p>
						<Button className="h-9" onClick={abrirNova}>
							<Plus className="h-4 w-4" />
							<span className="hidden sm:inline">Nova ferramenta</span>
							<span className="sm:hidden">Nova</span>
						</Button>
					</div>

					{ferramentas.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<Wrench className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">Nenhuma ferramenta criada</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Crie uma ferramenta para a IA consultar uma API de fora ou
								transferir o atendimento, e marque ela no agente.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{ferramentas.map((ferramenta) => (
								<div
									key={ferramenta.id}
									className="flex flex-col rounded-xl border bg-card p-4"
								>
									<div className="flex items-start justify-between gap-2">
										<div className="flex min-w-0 items-start gap-2.5">
											<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
												<Wrench className="h-4 w-4" />
											</span>
											<div className="min-w-0">
												<p className="truncate font-medium">{ferramenta.name}</p>
												<p className="truncate text-xs text-muted-foreground">
													{ferramenta.kind === "http"
														? `${ferramenta.method || "GET"} · ${ferramenta.url || ""}`
														: rotuloDaAcao(ferramenta.internalAction)}
												</p>
											</div>
										</div>

										<span
											className={
												ferramenta.status === "draft"
													? "shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400"
													: "shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
											}
										>
											{ferramenta.status === "draft" ? "Rascunho" : "Ativa"}
										</span>
									</div>

									<p className="mt-3 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
										{ferramenta.description}
									</p>

									<dl className="mt-3 space-y-1 text-xs">
										<div className="flex justify-between gap-2">
											<dt className="text-muted-foreground">Execução</dt>
											<dd className="truncate font-medium">
												{rotuloDoModo(ferramenta.executionMode)}
											</dd>
										</div>
										{ferramenta.credential && (
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Credencial</dt>
												<dd className="truncate font-medium">
													{ferramenta.credential.name}
												</dd>
											</div>
										)}
									</dl>

									<div className="mt-3 flex flex-wrap gap-1 border-t pt-3">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											aria-label={`Editar ${ferramenta.name}`}
											onClick={() => abrirEdicao(ferramenta)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											aria-label={`Remover ${ferramenta.name}`}
											onClick={() => {
												setSelecionada(ferramenta);
												setConfirmOpen(true);
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							))}
						</div>
				)}
				</>
			)}

			<Dialog open={modal} onOpenChange={() => setModal(false)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar ferramenta" : "Nova ferramenta"}
						</DialogTitle>
						<DialogDescription>
							A ferramenta é o que a IA pode chamar. O nome é como ela chama
							(call), a descrição é o que ela lê para decidir quando usar.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label htmlFor="ferramenta-nome">Nome</Label>
							<Input
								id="ferramenta-nome"
								value={form.name}
								onChange={(e) =>
									setForm({ ...form, name: e.target.value })
								}
								placeholder="segunda_via_boleto"
							/>
							<p className="text-xs text-muted-foreground">
								Minúsculas e _: é assim que a IA chama.
							</p>
							{erros.name && (
								<p className="text-xs text-destructive">{erros.name}</p>
							)}
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="ferramenta-descricao">Descrição</Label>
							<Textarea
								id="ferramenta-descricao"
								rows={3}
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
								placeholder="Busca a segunda via de um boleto pelo CPF do cliente."
							/>
							<p className="text-xs text-muted-foreground">
								É por ela que a IA decide quando usar a ferramenta.
							</p>
							{erros.description && (
								<p className="text-xs text-destructive">{erros.description}</p>
							)}
						</div>

						<div className="grid gap-3 sm:grid-cols-3">
							<div className="space-y-1.5">
								<Label>Tipo</Label>
								<Select
									value={form.kind}
									onValueChange={(v) =>
										setForm({
											...form,
											kind: v,
											internalTarget: "",
										})
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="http">API de fora</SelectItem>
										<SelectItem value="internal">Ação interna</SelectItem>
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-1.5">
								<Label>Modo de execução</Label>
								<Select
									value={form.executionMode}
									onValueChange={(v) =>
										setForm({ ...form, executionMode: v })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{MODOS.map((modo) => (
											<SelectItem key={modo.value} value={modo.value}>
												{modo.rotulo}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-1.5">
								<Label>Status</Label>
								<Select
									value={form.status}
									onValueChange={(v) => setForm({ ...form, status: v })}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{STATUS.map((status) => (
											<SelectItem key={status.value} value={status.value}>
												{status.rotulo}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">
									Rascunho não entra na lista do agente.
								</p>
							</div>
						</div>

						{/* ---------------- API de fora ---------------- */}
						{form.kind === "http" && (
							<>
								<div className="grid gap-3 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label>Método</Label>
										<Select
											value={form.method}
											onValueChange={(v) => setForm({ ...form, method: v })}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{METODOS.map((metodo) => (
													<SelectItem key={metodo} value={metodo}>
														{metodo}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="ferramenta-url">Endereço</Label>
										<Input
											id="ferramenta-url"
											value={form.url}
											onChange={(e) =>
												setForm({ ...form, url: e.target.value })
											}
											placeholder="https://api.exemplo.com/boleto/{{cpf}}"
										/>
										{erros.url && (
											<p className="text-xs text-destructive">{erros.url}</p>
										)}
									</div>
								</div>
								<p className="text-xs text-muted-foreground">
									Use {"{{parametro}}"} no endereço: o valor que a IA mandar
									entra no lugar.
								</p>

								<div className="space-y-1.5">
									<Label>Credencial</Label>
									<Select
										value={form.credentialId || "nenhuma"}
										onValueChange={(v) =>
											setForm({
												...form,
												credentialId: v === "nenhuma" ? "" : v,
											})
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="nenhuma">Nenhuma</SelectItem>
											{credenciais.map((credencial) => (
												<SelectItem
													key={credencial.id}
													value={String(credencial.id)}
												>
													{credencial.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-muted-foreground">
										O segredo nunca é escrito aqui: ele fica na credencial,
										cifrado no servidor.
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="ferramenta-cabecalhos">
										Cabeçalhos fixos
									</Label>
									<Textarea
										id="ferramenta-cabecalhos"
										rows={3}
										value={form.headers}
										onChange={(e) =>
											setForm({ ...form, headers: e.target.value })
										}
										placeholder={'{\n  "X-Origem": "atendimento"\n}'}
									/>
									<p className="text-xs text-muted-foreground">
										JSON. É o que a API de fora exige além da credencial.
									</p>
									{erros.headers && (
										<p className="text-xs text-destructive">{erros.headers}</p>
									)}
								</div>

								{METODOS_COM_CORPO.includes(form.method) && (
									<div className="space-y-1.5">
										<Label htmlFor="ferramenta-corpo">Corpo</Label>
										<Textarea
											id="ferramenta-corpo"
											rows={5}
											value={form.bodyTemplate}
											onChange={(e) =>
												setForm({ ...form, bodyTemplate: e.target.value })
											}
											placeholder={'{\n  "cpf": "{{cpf}}"\n}'}
										/>
										<p className="text-xs text-muted-foreground">
											JSON. {"{{parametro}}"} sozinho vira o valor com o
											tipo dele; dentro de um texto maior, vira texto.
										</p>
										{erros.bodyTemplate && (
											<p className="text-xs text-destructive">
												{erros.bodyTemplate}
											</p>
										)}
									</div>
								)}

								<div className="space-y-1.5">
									<Label htmlFor="ferramenta-resposta">
										Campos a manter da resposta
									</Label>
									<Textarea
										id="ferramenta-resposta"
										rows={3}
										value={form.responseMap}
										onChange={(e) =>
											setForm({ ...form, responseMap: e.target.value })
										}
										placeholder={"cliente.nome\nboletos[].venc"}
									/>
									<p className="text-xs text-muted-foreground">
										Um caminho por linha. Em branco, a IA recebe a
										resposta inteira — às vezes isso é mais do que
										precisa.
									</p>
								</div>
							</>
						)}

						{/* ---------------- ação interna ---------------- */}
						{form.kind === "internal" && (
							<div className="space-y-3">
								<div className="space-y-1.5">
									<Label>Ação</Label>
									<Select
										value={form.internalAction}
										onValueChange={(v) =>
											setForm({
												...form,
												internalAction: v,
												internalTarget: "",
											})
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{ACOES_INTERNAS.map((acao) => (
												<SelectItem key={acao.value} value={acao.value}>
													{acao.rotulo}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{chaveDoAlvo && (
									<div className="space-y-1.5">
										<Label>
											{form.internalAction === "transferir_fila"
												? "Fila de destino"
												: form.internalAction === "adicionar_etiqueta"
													? "Etiqueta"
													: "Fluxo"}
										</Label>
										<Select
											value={form.internalTarget || "nenhum"}
											onValueChange={(v) =>
												setForm({
													...form,
													internalTarget: v === "nenhum" ? "" : v,
												})
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="nenhum">Escolher</SelectItem>
												{alvoDaAcao.map((item) => (
													<SelectItem key={item.id} value={String(item.id)}>
														{item.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{erros.internalTarget && (
											<p className="text-xs text-destructive">
												{erros.internalTarget}
											</p>
										)}
									</div>
								)}

								<p className="text-xs text-muted-foreground">
									{form.internalAction === "chamar_humano"
										? "A IA pede um atendente e para de responder."
										: "A ação acontece dentro do atendimento, no ticket que está aberto."}
								</p>
							</div>
						)}

						{/* ---------------- parâmetros ---------------- */}
						<div className="space-y-2">
							<div className="flex items-center justify-between gap-2">
								<Label>Parâmetros que a IA pode mandar</Label>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-7 text-xs"
									onClick={() =>
										setForm({
											...form,
											parameters: [
												...form.parameters,
												{ ...PARAMETRO_VAZIO },
											],
										})
									}
								>
									<Plus className="h-3 w-3" />
									Parâmetro
								</Button>
							</div>

							{form.parameters.length === 0 && (
								<p className="text-xs text-muted-foreground">
									Sem parâmetro a ferramenta não recebe nada. Serve para o
									cliente escrever o endereço na mão.
								</p>
							)}

							{form.parameters.map((parametro, indice) => (
								<div
									key={indice}
									className="space-y-2 rounded-lg border p-3"
								>
									<div className="flex flex-wrap items-center gap-2">
										<Input
											className="h-8 min-w-[140px] flex-1"
											value={parametro.name}
											onChange={(e) =>
												atualizarParametro(indice, { name: e.target.value })
											}
											placeholder="cpf"
											aria-label="Nome do parâmetro"
										/>
										<Select
											value={parametro.type}
											onValueChange={(v) =>
												atualizarParametro(indice, { type: v })
											}
										>
											<SelectTrigger
												className="h-8 w-[150px]"
												aria-label="Tipo do parâmetro"
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{TIPOS_DE_PARAMETRO.map((tipo) => (
													<SelectItem key={tipo.value} value={tipo.value}>
														{tipo.rotulo}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-destructive"
											aria-label="Remover parâmetro"
											onClick={() => removerParametro(indice)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>

									<div className="flex flex-wrap items-center gap-3">
										<label className="flex items-center gap-1.5 text-xs">
											<Checkbox
												checked={Boolean(parametro.required)}
												onCheckedChange={(v) =>
													atualizarParametro(indice, {
														required: Boolean(v),
													})
												}
											/>
											Obrigatório
										</label>

										<div className="flex min-w-[200px] flex-1 items-center gap-2">
											<span className="shrink-0 text-xs text-muted-foreground">
												Preencher com
											</span>
											<Select
												value={parametro.fixedFrom || "nenhum"}
												onValueChange={(v) =>
													atualizarParametro(indice, {
														fixedFrom: v === "nenhum" ? "" : v,
													})
												}
											>
												<SelectTrigger
													className="h-8"
													aria-label="Preencher com"
												>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{ORIGENS_FIXAS.map((origem) => (
														<SelectItem key={origem.value} value={origem.value}>
															{origem.rotulo}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>

									<Input
										className="h-8"
										value={parametro.description}
										onChange={(e) =>
											atualizarParametro(indice, {
												description: e.target.value,
											})
										}
										placeholder="O que a IA precisa saber para preencher isto"
										aria-label="Descrição do parâmetro"
									/>
								</div>
							))}

							{erros.parametros && (
								<p className="text-xs text-destructive">{erros.parametros}</p>
							)}
						</div>

						{/* ---------------- teste ---------------- */}
						{form.kind === "http" && (
							<div className="space-y-2 rounded-lg border p-3">
								<div className="flex items-center gap-2">
									<FlaskConical className="h-4 w-4 text-muted-foreground" />
									<p className="text-sm font-medium">Testar a chamada</p>
								</div>

								{!ferramentaSalva ? (
									<p className="text-xs text-muted-foreground">
										Salve a ferramenta antes de testar: o teste chama a
										versão gravada.
									</p>
								) : (
									<>
										{parametrosVisiveis.map((parametro) => (
											<div
												key={parametro.name}
												className="space-y-1.5"
											>
												<Label htmlFor={`teste-${parametro.name}`}>
													{parametro.name}
													{parametro.required ? " *" : ""}
												</Label>
												<Input
													id={`teste-${parametro.name}`}
													type={
														parametro.type === "number" ||
														parametro.type === "integer"
															? "number"
															: "text"
													}
													value={testeParams[parametro.name] ?? ""}
													onChange={(e) =>
														setTesteParams({
															...testeParams,
															[parametro.name]: e.target.value,
														})
													}
												/>
											</div>
										))}

										{ferramentaSalva.isAction && (
											<label className="flex items-center gap-2 text-xs">
												<Checkbox
													checked={permitirAcao}
													onCheckedChange={(v) =>
														setPermitirAcao(Boolean(v))
													}
												/>
												Permitir ação de verdade (esta chamada muda
												alguma coisa na API de fora)
											</label>
										)}

										<Button
											type="button"
											variant="outline"
											size="sm"
											className="h-8 text-xs"
											disabled={teste.rodando}
											onClick={testar}
										>
											{teste.rodando ? (
												<Loader2 className="h-3 w-3 animate-spin" />
											) : (
												<FlaskConical className="h-3 w-3" />
											)}
											Testar
										</Button>

										{teste.resultado && (
											<pre
												className={
													teste.falhou
														? "max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-md bg-destructive/5 p-3 text-[11px] leading-relaxed text-destructive"
														: "max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-[11px] leading-relaxed"
												}
											>
												{teste.resultado}
											</pre>
										)}
									</>
								)}
							</div>
						)}
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(false)}>
							Cancelar
						</Button>
						<Button onClick={salvar} disabled={salvando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{form.id ? "Salvar" : "Criar ferramenta"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AbaFerramentas;
